import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  ArrowLeft, Save, Plus, ChevronDown, ChevronUp,
  FileText, Package, MapPin, Calendar, Building2,
  Truck, Globe, Check, X, Hash, User, DollarSign, Send,
  CheckCircle2,
  ChevronLeft,
} from 'lucide-react';
import { useDispatch, useSelector } from "react-redux";
import moment from 'moment';

import { getSalesDetails, addSalesDetails, updateSalesDetails } from '../../redux/Sales/actions';
import { getAddressAPI } from '@/redux/Purchase/actions';
import { SearchBar } from '../SearchBox/SearchBar';
import FileUpload from '../FileUpload/FileUpload';
import { validateForm } from "../ui/useFormValidation";
import DatePicker from 'react-datepicker';
import { DataTable } from '../ui/table';
import { getItemColumns } from './columns';
import { showToast } from '@/redux/Toast/actions';
import CelebrationButton from '../ui/CelebrationButton/CelebrationButton';
import { callLoader } from '@/redux/Loader/actions';
import PageHeader from '../FormHeader/formPageHeader';

/* ─── tiny helpers ─── */
const InputField = ({ label, required, children, className = '' }) => (
  <div className={className}>
    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const SECTION_COLORS = {
  blue:   'bg-blue-500',
  teal:   'bg-teal-500',
  violet: 'bg-violet-500',
  cyan:   'bg-cyan-500',
  green:  'bg-emerald-500',
  indigo: 'bg-indigo-500',
  amber:  'bg-amber-500',
  orange: 'bg-orange-500',
  rose:   'bg-rose-500',
  dark:   'bg-slate-700',
};

/* ─── main component ─── */
const SalesOrderForm = ({ onBack, orderDataId = null }) => {

  const dispatch = useDispatch();
  const [formData, setFormData] = useState();
  const [items, setItems] = useState([]);
  const [errors, setErrors] = useState({});

  const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");
  const userName = authUser?.username;
  const companyId = authUser?.companyId;

  const { countryList, incoTermsList, quantityTypeList, uomTypeList } = useSelector((state) => state.common);
  const { isAnimate } = useSelector((state) => state.toast);

  /* section expand state — all open by default */
  const [expandedSections, setExpandedSections] = useState({
    orderDetails: true,
    tradePartner: true,
    routing:      true,
    commodity:    true,
  });

  /* scroll tracking */
  const [currentStep, setCurrentStep] = useState(0);
  const orderDetailsRef = useRef(null);
  const tradePartnerRef = useRef(null);
  const routingRef      = useRef(null);
  const commodityRef    = useRef(null);

  const sectionRefs = {
    orderDetails: orderDetailsRef,
    tradePartner: tradePartnerRef,
    routing:      routingRef,
    commodity:    commodityRef,
  };

  const steps = ['Order Details', 'Trade Partner', 'Routing', 'Commodity'];
  const stepSectionMap = useMemo(() => ['orderDetails', 'tradePartner', 'routing', 'commodity'], []);

  /* intersection observer for step highlighting */
  useEffect(() => {
    const observers = [];
    stepSectionMap.forEach((key, index) => {
      const ref = sectionRefs[key];
      if (!ref?.current) return;
      const observer = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setCurrentStep(index); },
        { root: null, rootMargin: '-30% 0px -60% 0px', threshold: 0 }
      );
      observer.observe(ref.current);
      observers.push(observer);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [formData, stepSectionMap]);

  const scrollToSection = (index) => {
    const key = stepSectionMap[index];
    const ref = sectionRefs[key];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setExpandedSections((p) => ({ ...p, [key]: true }));
    }
  };

  const toggleSection = (section) =>
    setExpandedSections((p) => ({ ...p, [section]: !p[section] }));

  /* ─── data helpers (ALL LOGIC UNCHANGED) ─── */
  useEffect(() => {
    if (orderDataId != 0 && orderDataId != null) {
      _getSalesOrder();
    } else {
      _setNewSalesOrder();
    }
  }, [orderDataId]);

  const _getSalesOrder = async () => {
    try {
      dispatch(callLoader(1));
      const resultData = await dispatch(getSalesDetails(orderDataId));
      console.log(resultData);
      setFormData(resultData);
      setItems(resultData?.lineItems ?? []);
      dispatch(callLoader(0));
      console.log(moment(resultData?.poDate).format('YYYY-MM-DD'));
    } catch (error) {
      dispatch(callLoader(0));
    }
  };

  const _setNewSalesOrder = async () => {
    setFormData(null);
    setItems([{
      salesOrderLineItemId: 0,
      sku: '',
      skuItemDesc: '',
      uot: 0,
      casesPerUot: 0,
      remainingUnit: 0,
      remainingCases: 0,
      caseCount: 0,
      caseCountUOM: '',
      packing: '0.00',
      quantity: '0.00',
      metricId: 0,
      metricType: '',
      price: 0,
      total: 0,
      warehouse: '',
      notes: ''
    }]);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    if (field === 'quantity' || field === 'price') {
      const qty = parseFloat(updated[index].quantity) || 0;
      const price = parseFloat(updated[index].price) || 0;
      updated[index].total = (qty * price).toFixed(2);
    }
    setItems(updated);
  };

  const addItem = () => {
    setItems([...items, {
      id: items.length + 1,
      sno: items.length + 1,
      skuCode: '',
      itemDesc: '',
      uot: '0.00',
      casesPerUot: '0.00',
      remainingUnit: '',
      remainingCases: '',
      caseCount: '0.00',
      uomcd: 'SELECT UOMCD',
      packing: '0.00',
      quantity: '0.00',
      unit: 'TON',
      price: '0.00',
      total: '0.00',
      source: 'Manual',
      notes: ''
    }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const _setAddress = async (type, id) => {
    const address = await getAddressAPI(id);
    setFormData(prev => ({ ...prev, [`${type}`]: address }));
  };

  const validationConfig = {
    soNumber:         { required: true,  label: "Sales Order" },
    customerId:       { required: true,  label: "Customer" },
    payTerms:         { required: true,  label: "Pay Terms" },
    documentTo:       { required: true,  label: "Document To" },
    warehouseId:      { required: true,  label: "Warehouse" },
    warehouseAddress: { required: true,  label: "Warehouse Address" },
    pol:              { required: true,  label: "POL" },
    destination:      { required: true,  label: "Destination" },
    pod:              { required: true,  label: "POD" },
    destinationCountry: { required: false },
    soDate:           { required: true,  label: "SO Date" },
    shipByDate:       { required: true,  label: "Ship By Date" },
    remarks:          { required: false },
    incotermId:       { required: false },
    salesPersonId:    { required: false },
  };

  const handleSubmit = async (e) => {
    try {
      var itmPayload = [];
      items.map((v, i) => {
        const ob = {
          "salesorder_lineitem_ID": v?.salesOrderLineItemId ?? 0,
          "sku": v?.sku,
          "sku_item_desc": v?.skuItemDesc,
          "uot": v?.uot,
          "cases_per_uot": v?.casesPerUot,
          "remaining_unit": v?.remainingUnit,
          "remaining_cases": v?.remainingCases,
          "uom_type_ID": v?.uomTypeId,
          "uom_type": v?.uomType,
          "packing": v?.packing,
          "quantity": v?.quantity,
          "metric_ID": v?.metricId,
          "metric_type": v?.metricType,
          "price": v?.price,
          "source": v?.source,
          "notes": v?.notes,
          "load_status": v?.loadStatus,
          "created_by": v?.createdBy ?? userName,
          "is_active": v?.isActive != false ? true : false,
        };
        itmPayload.push(ob);
      });
      const payload = {
        "so_ID": orderDataId ?? 0,
        "company_ID": companyId,
        "warehouse_ID": formData?.warehouseId,
        "warehouse": formData?.warehouse,
        "warehouse_address": formData?.warehouseAddress,
        "pol": formData?.pol,
        "pod": formData?.pod,
        "destination": formData?.destination,
        "document_to": formData?.documentTo,
        "so_date": formData?.soDate,
        "ship_by_date": formData?.shipByDate,
        "customer_ID": formData?.customerId,
        "customer": formData?.customer,
        "customer_address": formData?.customerAddress,
        "pay_terms": formData?.payTerms,
        "incoterm_ID": formData?.incotermId,
        "incoterm": formData?.incoterm,
        "destination_country": formData?.destinationCountry,
        "assign_to_ID": formData?.assignToId,
        "assign_to_name": formData?.assignToName,
        "sales_person_ID": formData?.salesPersonId,
        "sales_person": formData?.salesPerson,
        "remarks": formData?.remarks,
        "created_by": formData?.createdBy ?? userName,
        "ar_hold": formData?.arHold,
        "is_active": formData?.isActive != false ? true : false,
        "lineItems": itmPayload
      };

      const validationErrors = validateForm({
        data: formData,
        items,
        rules: validationConfig,
      });

      setErrors(validationErrors);

      if (Object.keys(validationErrors).length > 0) {
        return;
      }

      dispatch(callLoader(1));
      if (orderDataId != 0 && orderDataId != null) {
        const updateDetails = await updateSalesDetails(payload);
        console.log(updateDetails);
      } else {
        const addDetails = await addSalesDetails(payload);
        console.log(addDetails);
      }
      dispatch(callLoader(0));
      dispatch(showToast("Record saved successfully", "success", true));

    } catch (e) {
      console.log('Error in submitting sales order:', e);
      dispatch(showToast("Error in submitting sales order", "error"));
      dispatch(callLoader(0));
    }
  };

  const formhandleSubmit = async (e) => {
    e.preventDefault();
  };

  /* isSectionFilled */
  const isSectionFilled = useCallback((key) => {
    if (!formData) return false;
    switch (key) {
      case 'orderDetails': return !!(formData?.soNumber || formData?.incotermId || formData?.soDate);
      case 'tradePartner': return !!(formData?.customer || formData?.warehouse || formData?.documentTo);
      case 'routing':      return !!(formData?.pol || formData?.pod || formData?.destination);
      case 'commodity':    return !!(items?.length > 0 && items.some(i => i.sku));
      default: return false;
    }
  }, [formData, items]);

  /* ─── Section Header ─── */
  const SectionHeader = ({ icon: Icon, title, color = 'blue', section, badge, action }) => {
    const iconBg = SECTION_COLORS[color] ?? color ?? SECTION_COLORS.blue;
    return (
      <button
        onClick={() => toggleSection(section)}
        className="w-full flex items-center justify-between p-4 border-b bg-gradient-to-r to-slate-100 dark:from-slate-800 dark:to-slate-700 cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 mx-2 rounded-lg shadow-md ${iconBg}`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-200">{title}</h2>
          {badge && (
            <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-medium rounded-full">
              {badge}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {action}
          <div className={`p-1.5 rounded-md transition-colors ${expandedSections[section] ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'bg-slate-200 dark:bg-slate-700'}`}>
            {expandedSections[section]
              ? <ChevronUp className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              : <ChevronDown className="w-4 h-4 text-slate-500 dark:text-slate-400" />}
          </div>
        </div>
      </button>
    );
  };

  const options = (countryList || [])?.map((loc) => {
    const match = (loc?.incoterm || "").match(/\(([^)]+)\)/);
    const code = match ? match[1] : null;
    return { value: code, label: loc?.incoterm };
  });

  const columns = getItemColumns({ items, handleItemChange, uomTypeList, quantityTypeList });

  /* shared input classes */
  const inputClass = 'w-full px-3 py-[0.6rem] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all';
  const selectClass = 'w-full px-3 py-[0.5rem] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none cursor-pointer';

  /* ══════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════ */
  return (
    <div className="h-full bg-slate-50 dark:bg-slate-950">

      {/* ── STICKY PAGE HEADER ── */}
      <div className="page-header-sticky sticky top-0 z-50">
        <PageHeader
          icon={<FileText className="w-5 h-5 text-white" />}
          title="Sales Order"
          badge={orderDataId ? 'Editing Sales Order' : 'New Sales Order'}
          subtitle={
            orderDataId
              ? <>{'SO No. '}<span className="font-semibold text-slate-700 dark:text-slate-300">{formData?.soNumber}</span></>
              : 'Create and manage sales orders'
          }
          actions={[
            { label: 'Back', icon: <ArrowLeft className="w-4 h-4" />, onClick: onBack, variant: 'ghost' },
          ]}
          onSubmit={handleSubmit}
          submitLabel="Submit Order"
          isSuccess={isAnimate}
        />
        <div style={{ height: 'var(--page-header-height)' }} />
      </div>

      <div className="p-4 space-y-3 pb-16">

        {/* ── PROGRESS STEPS ── */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 px-4 py-3 overflow-x-auto">
          <div className="flex items-center w-full justify-between gap-0">
            {steps.map((step, index) => {
              const key    = stepSectionMap[index];
              const filled = isSectionFilled(key);
              const done   = filled && index !== currentStep;
              const active = index === currentStep;
              return (
                <React.Fragment key={step}>
                  <div
                    className={[
                      'flex items-center gap-2 h-8 pl-2 pr-3 rounded-full text-xs font-medium cursor-pointer transition-all duration-200 select-none',
                      done   && 'text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20',
                      active && 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
                      !done && !active && 'text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800',
                    ].filter(Boolean).join(' ')}
                    onClick={() => scrollToSection(index)}
                  >
                    <span className={[
                      'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0',
                      done   && 'bg-green-500 text-white',
                      active && 'bg-blue-600 text-white',
                      !done && !active && 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400',
                    ].filter(Boolean).join(' ')}>
                      {done ? <Check className="w-3 h-3" /> : index + 1}
                    </span>
                    <span className="hidden sm:inline">{step}</span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={[
                      'flex-1 h-0.5 min-w-[8px] mx-1 rounded-full transition-colors duration-300',
                      done ? 'bg-green-400 dark:bg-green-600' : 'bg-slate-200 dark:bg-slate-700',
                    ].join(' ')} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <form id="so-form" onSubmit={formhandleSubmit} className="space-y-3">

          {/* ── ORDER DETAILS ── */}
          <div
            ref={orderDetailsRef}
            className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
          >
            <SectionHeader icon={Hash} title="Order Details" color="blue" section="orderDetails" />
            {expandedSections.orderDetails && (
              <div className="p-4">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">

                  <InputField label="Sales Order #" required>
                    <input
                      type="text"
                      name="salesOrderNo"
                      value={formData?.soNumber || ''}
                      onChange={handleInputChange}
                      readOnly
                      className={[
                        inputClass,
                        'bg-slate-50 dark:bg-slate-900 cursor-not-allowed',
                        errors.soNumber ? 'border-red-500' : '',
                      ].join(' ')}
                    />
                  </InputField>

                  <InputField label="Inco Terms">
                    <select
                      name="incotermId"
                      value={formData?.incotermId || ''}
                      onChange={(e) => {
                        handleInputChange({ target: { name: 'incotermId', value: e.target.value } });
                        handleInputChange({ target: { name: 'incoterm', value: e.target.options[e.target.selectedIndex].text } });
                      }}
                      className={selectClass}
                    >
                      <option value="">SELECT</option>
                      {incoTermsList?.map((term, key) => (
                        <option key={key} value={term?.id}>{term?.incoterm}</option>
                      ))}
                    </select>
                  </InputField>

                  <InputField label="Sales Person">
                    <SearchBar
                      searchAPI={'v1/AutoSuggestion/GetVendorSuggestion?Prefix='}
                      selectReacord={(d) => {
                        handleInputChange({ target: { name: 'salesPerson', value: d?.[1] } });
                        handleInputChange({ target: { name: 'salesPersonId', value: d?.[0] } });
                      }}
                      value={formData?.salesPerson}
                    />
                  </InputField>

                  <InputField label="SO Date" required>
                    <DatePicker
                      selected={formData?.soDate ? new Date(formData.soDate) : null}
                      showIcon
                      onChange={(date) =>
                        handleInputChange({ target: { name: 'soDate', value: moment(date).format('YYYY-MM-DD') } })
                      }
                      dateFormat="dd MMM yyyy"
                      placeholderText="Select Date"
                      className={inputClass}
                    />
                  </InputField>

                  <InputField label="Ship By Date" required>
                    <DatePicker
                      selected={formData?.shipByDate ? new Date(formData.shipByDate) : null}
                      showIcon
                      onChange={(date) =>
                        handleInputChange({ target: { name: 'shipByDate', value: moment(date).format('YYYY-MM-DD') } })
                      }
                      dateFormat="dd MMM yyyy"
                      placeholderText="Select Date"
                      className={inputClass}
                    />
                  </InputField>

                  <InputField label="AR Hold">
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="checkbox"
                        name="arHold"
                        checked={formData?.arHold || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-600 dark:text-slate-400">Mark AR Hold</span>
                    </div>
                  </InputField>

                </div>
              </div>
            )}
          </div>

          {/* ── TRADE PARTNER + ROUTING side by side ── */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">

            {/* Trade Partner */}
            <div
              ref={tradePartnerRef}
              className="xl:col-span-2 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
            >
              <SectionHeader icon={Building2} title="Trade Partner" color="cyan" section="tradePartner" />
              {expandedSections.tradePartner && (
                <div className="p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

                    {/* Customer */}
                    <div>
                      <InputField label="Customer" required>
                        <SearchBar
                          searchAPI={'v1/AutoSuggestion/GetCustomerSuggestion?Prefix='}
                          selectReacord={(d) => {
                            handleInputChange({ target: { name: 'customer', value: d?.[1] } });
                            handleInputChange({ target: { name: 'customerId', value: d?.[0] } });
                            _setAddress('customerAddress', d?.[0]);
                          }}
                          value={formData?.customer}
                        />
                      </InputField>
                      <textarea
                        name="customerAddress"
                        value={formData?.customerAddress || ''}
                        onChange={handleInputChange}
                        rows="5"
                        placeholder="Customer address..."
                        className={inputClass + ' mt-2 resize-none'}
                      />
                    </div>

                    {/* Pay Terms + Document To */}
                    <div className="space-y-4">
                      <InputField label="Pay Terms" required>
                        <select
                          name="payTerms"
                          value={formData?.payTerms || ''}
                          onChange={handleInputChange}
                          className={[selectClass, errors.payTerms ? 'border-red-500' : ''].join(' ')}
                        >
                          <option value="">SELECT PAY TERMS</option>
                          <option value="NET30">Net 30</option>
                          <option value="NET60">Net 60</option>
                          <option value="NET90">Net 90</option>
                          <option value="COD">Cash on Delivery</option>
                          <option value="PREPAID">Prepaid</option>
                          <option value="DUE_ON_RECEIPT">Due on Receipt</option>
                        </select>
                      </InputField>

                      <InputField label="Document To" required>
                        <SearchBar
                          searchAPI={'v1/AutoSuggestion/GetVendorSuggestion?Prefix='}
                          selectReacord={(d) => {
                            handleInputChange({ target: { name: 'documentTo', value: d?.[1] } });
                          }}
                          value={formData?.documentTo}
                        />
                      </InputField>
                    </div>

                    {/* Warehouse */}
                    <div>
                      <InputField label="Warehouse" required>
                        <SearchBar
                          searchAPI={'v1/AutoSuggestion/GetWarehouseSuggestion?Prefix='}
                          selectReacord={(d) => {
                            handleInputChange({ target: { name: 'warehouse', value: d?.[1] } });
                            handleInputChange({ target: { name: 'warehouseId', value: d?.[0] } });
                            _setAddress('warehouseAddress', d?.[0]);
                          }}
                          value={formData?.warehouse}
                        />
                      </InputField>
                      <textarea
                        name="warehouseAddress"
                        value={formData?.warehouseAddress || ''}
                        onChange={handleInputChange}
                        rows="5"
                        placeholder="Warehouse address..."
                        className={inputClass + ' mt-2 resize-none'}
                      />
                    </div>

                    {/* Remarks — spans 2 cols */}
                    <div className="sm:col-span-2">
                      <InputField label="Remarks">
                        <textarea
                          name="remarks"
                          value={formData?.remarks || ''}
                          onChange={handleInputChange}
                          rows="3"
                          placeholder="Enter remarks..."
                          className={inputClass + ' resize-none'}
                        />
                      </InputField>
                    </div>

                  </div>
                </div>
              )}
            </div>

            {/* Routing */}
            <div
              ref={routingRef}
              className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
            >
              <SectionHeader icon={Truck} title="Routing" color="orange" section="routing" />
              {expandedSections.routing && (
                <div className="p-4 space-y-4">

                  <InputField label="POL" required>
                    <SearchBar
                      searchAPI={'v1/AutoSuggestion/GetDestinationSuggestion?Prefix='}
                      selectReacord={(d) => {
                        handleInputChange({ target: { name: 'pol', value: d?.[1] } });
                      }}
                      value={formData?.pol}
                    />
                  </InputField>

                  <InputField label="POD" required>
                    <SearchBar
                      searchAPI={'v1/AutoSuggestion/GetDestinationSuggestion?Prefix='}
                      selectReacord={(d) => {
                        handleInputChange({ target: { name: 'pod', value: d?.[1] } });
                      }}
                      value={formData?.pod}
                    />
                  </InputField>

                  <InputField label="Destination" required>
                    <SearchBar
                      searchAPI={'v1/AutoSuggestion/GetDestinationSuggestion?Prefix='}
                      selectReacord={(d) => {
                        handleInputChange({ target: { name: 'destination', value: d?.[1] } });
                      }}
                      value={formData?.destination}
                    />
                  </InputField>

                  <InputField label="Dest. Country">
                    <select
                      name="destinationCountry"
                      value={formData?.destinationCountry || ''}
                      onChange={handleInputChange}
                      className={selectClass}
                    >
                      <option value="">SELECT COUNTRY OF DESTINATION</option>
                      {options && options.map((item) => (
                        <option key={item?.value} value={item?.value}>{item?.label}</option>
                      ))}
                    </select>
                  </InputField>

                </div>
              )}
            </div>
          </div>

          {/* ── COMMODITY TABLE ── */}
          <div
            ref={commodityRef}
            className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
          >
            <SectionHeader
              icon={Package}
              title="Commodity"
              color="green"
              section="commodity"
              badge={`${items.filter(i => i?.isActive !== false).length} items`}
              action={
                <button
                  type="button"
                  onClick={addItem}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Add Row</span>
                </button>
              }
            />
            {expandedSections.commodity && (
              <DataTable
                data={items.filter((i) => i?.isActive !== false)}
                columns={columns}
                tdClass="px-2 py-2"
                thClass="px-2 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-400"
                trClass="hover:bg-slate-50 dark:hover:bg-slate-800/30"
                htrClass="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50"
                boxClass="overflow-x-auto overflow-y-visible pb-[20vh] text-xs"
              />
            )}
          </div>

          {/* File Upload */}
          {orderDataId != 0 && orderDataId != null && (
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden p-4">
              <FileUpload orderId={orderDataId} orderNum={formData?.soNumber} type="sales" />
            </div>
          )}

        </form>

        {/* ── BOTTOM ACTION BAR ── */}
        <div className="p-3 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-500 dark:text-slate-400">
              <span className="text-red-500">*</span> Required fields must be filled
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 text-slate-600 hover:bg-red-50 dark:text-slate-400 dark:hover:bg-slate-800"
              >
                <X className="w-4 h-4" />
                <span className="hidden sm:inline">Cancel</span>
              </button>

              <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1" />

              <CelebrationButton
                onClick={() => handleSubmit()}
                label="Submit Order"
                className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 bg-green-600 text-white hover:bg-green-700"
                icon={<Send className="w-4 h-4" />}
                isSuccess={isAnimate}
              />
            </div>
          </div>
        </div>

      </div>{/* /p-4 */}
    </div>
  );
};

export default SalesOrderForm;